
import { useState, useRef, useEffect } from "react";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription, 
  CardFooter 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, Sparkles, Clock, LucideIcon, TrendingUp, Copy, Check, ThumbsUp, ThumbsDown, DollarSign, Users, BarChart3, Package, ArrowRight, StopCircle, Save, Download, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRestaurant } from "@/contexts/RestaurantContext";
import { chatService } from "@/services/api/chat";
import { feedbackService } from "@/services/api/feedback";
import { analysisInsightsApiService, AnalysisInsight } from "@/services/api/analysisInsights";
import { toast } from "sonner";
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

interface SuggestionProps {
  title: string;
  description: string;
  icon: LucideIcon;
  queryTemplate: string;
  hasInput?: boolean;
  inputPlaceholder?: string;
  onSend: (query: string) => void;
}

const Suggestion = ({ title, description, icon: Icon, queryTemplate, hasInput = false, inputPlaceholder = "Enter product", onSend }: SuggestionProps) => {
  const [inputValue, setInputValue] = useState("");
  
  const handleSend = () => {
    const query = hasInput && inputValue 
      ? queryTemplate.replace("{input}", inputValue)
      : queryTemplate;
    onSend(query);
    setInputValue("");
  };

  return (
    <div className="flex flex-col p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors">
      <div className="flex items-start gap-2 mb-2">
        <div className="p-1.5 rounded-full bg-primary/10">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-sm">{title}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>
      {hasInput && (
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={inputPlaceholder}
          className="h-8 text-sm mb-2"
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
        />
      )}
      <Button
        onClick={handleSend}
        size="sm"
        variant="outline"
        className="w-full h-8 text-xs"
      >
        <ArrowRight className="h-3 w-3 mr-1" />
        Send to chat
      </Button>
    </div>
  );
};

const MessageBubble = ({ message }: { message: Message }) => {
  const [copied, setCopied] = useState(false);
  const [showCopyButton, setShowCopyButton] = useState(false);
  const [feedback, setFeedback] = useState<'like' | 'dislike' | null>(null);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleFeedback = async (type: 'like' | 'dislike') => {
    setFeedback(type);
    
    try {
      await feedbackService.submitFeedback(message.id, type);
      console.log(`✅ Feedback submitted for message ${message.id}: ${type}`);
    } catch (error) {
      console.error('❌ Failed to submit feedback:', error);
      // Optionally revert the feedback state on error
      // setFeedback(null);
    }
  };

  return (
    <div 
      className={cn(
        "group flex items-start gap-3 max-w-[85%] mb-4",
        message.isUser ? "ml-auto flex-row-reverse" : "mr-auto"
      )}
      onMouseEnter={() => setShowCopyButton(true)}
      onMouseLeave={() => setShowCopyButton(false)}
    >
      {/* Avatar */}
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
        message.isUser 
          ? "bg-primary text-primary-foreground" 
          : "bg-muted"
      )}>
        {message.isUser ? (
          <span className="text-xs font-medium">U</span>
        ) : (
          <Bot className="h-4 w-4" />
        )}
      </div>
      
      {/* Message bubble */}
      <div className={cn(
        "relative rounded-2xl px-4 py-3 shadow-sm",
        message.isUser 
          ? "bg-primary text-primary-foreground rounded-br-md" 
          : "bg-muted text-foreground rounded-bl-md"
      )}>
        <div className="text-sm leading-relaxed">
          {message.isUser ? (
            <div className="whitespace-pre-line">{message.content}</div>
          ) : (
            <ReactMarkdown
              className="prose prose-sm prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
              components={{
                h1: ({node, ...props}) => <h1 className="text-lg font-semibold mb-2 text-current" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-base font-semibold mb-2 text-current" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-sm font-semibold mb-1 text-current" {...props} />,
                p: ({node, ...props}) => <p className="mb-2 text-current" {...props} />,
                ul: ({node, ...props}) => <ul className="mb-2 ml-4 list-disc text-current" {...props} />,
                ol: ({node, ...props}) => <ol className="mb-2 ml-4 list-decimal text-current" {...props} />,
                li: ({node, ...props}) => <li className="mb-1 text-current" {...props} />,
                strong: ({node, ...props}) => <strong className="font-semibold text-current" {...props} />,
                em: ({node, ...props}) => <em className="italic text-current" {...props} />,
                code: ({node, ...props}) => <code className="bg-background/20 px-1 py-0.5 rounded text-xs font-mono text-current" {...props} />,
                pre: ({node, ...props}) => <pre className="bg-background/20 p-2 rounded text-xs font-mono overflow-x-auto mb-2 text-current" {...props} />,
                blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-current/30 pl-4 my-2 text-current/80" {...props} />
              }}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>
        
        {/* Copy button */}
        {!message.isUser && (showCopyButton || copied) && (
          <button
            onClick={handleCopy}
            className={cn(
              "absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all",
              "bg-background border shadow-sm hover:shadow-md",
              copied ? "bg-green-500 text-white" : "hover:bg-accent"
            )}
          >
            {copied ? (
              <Check className="h-3 w-3" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </button>
        )}
        
        {/* Timestamp and Feedback buttons */}
        <div className="flex items-center justify-between mt-2">
          <div className={cn(
            "text-xs opacity-70",
            message.isUser ? "text-primary-foreground/70" : "text-muted-foreground"
          )}>
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
          
          {/* Like/Dislike buttons for assistant messages */}
          {!message.isUser && (
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => handleFeedback('like')}
                className={cn(
                  "p-1 rounded-full transition-colors hover:bg-background/50",
                  feedback === 'like' 
                    ? "bg-green-100 text-green-600" 
                    : "text-muted-foreground hover:text-green-600"
                )}
                title="This response was helpful"
              >
                <ThumbsUp className="h-3 w-3" />
              </button>
              <button
                onClick={() => handleFeedback('dislike')}
                className={cn(
                  "p-1 rounded-full transition-colors hover:bg-background/50",
                  feedback === 'dislike' 
                    ? "bg-red-100 text-red-600" 
                    : "text-muted-foreground hover:text-red-600"
                )}
                title="This response was not helpful"
              >
                <ThumbsDown className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AIAssistant = () => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const { restaurantProfile, isLoading: isLoadingProfile } = useRestaurant();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Analysis saving state
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [analysisForm, setAnalysisForm] = useState<{
    originalQuery: string;
    analysisText: string;
    analysisType: string;
    importance: 'low' | 'medium' | 'high';
    tags: string[];
    recommendations: string[];
    dataSummary: Record<string, any>;
  }>({
    originalQuery: '',
    analysisText: '',
    analysisType: 'general_analysis',
    importance: 'medium',
    tags: [],
    recommendations: [],
    dataSummary: {}
  });
  const [isSaving, setIsSaving] = useState(false);
  
  // Convert messages to chat history format for API
  const getChatHistory = (): ChatHistoryItem[] => {
    return messages.map(msg => ({
      role: msg.isUser ? 'user' : 'assistant',
      content: msg.content
    }));
  };

  const addMessage = (content: string, isUser: boolean) => {
    const newMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, newMessage]);
  };

  // Auto-scroll to bottom when shouldAutoScroll is true
  useEffect(() => {
    if (shouldAutoScroll && chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
      setShouldAutoScroll(false);
    }
  }, [messages, shouldAutoScroll]);

  const handleSend = async () => {
    if (!input.trim() || !restaurantProfile) return;
    
    const userMessage = input;
    const chatHistory = getChatHistory();
    addMessage(userMessage, true);
    setInput("");
    setIsLoading(true);
    setShouldAutoScroll(true); // Trigger auto-scroll when user sends message

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await chatService.sendMessage(
        userMessage, 
        restaurantProfile.business_unit_id, 
        chatHistory,
        abortControllerRef.current.signal
      );
      addMessage(response, false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        addMessage('Query cancelled by user.', false);
      } else {
        console.error('Error sending message:', error);
        addMessage('Sorry, I encountered an error. Please try again.', false);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion: string) => {
    if (!restaurantProfile) return;
    
    setInput(suggestion);
    // Optionally auto-send or let user review and send manually
    // For now, let's just copy to input so user can review
  };

  const handleSuggestionSend = async (query: string) => {
    if (!restaurantProfile) return;
    
    const chatHistory = getChatHistory();
    addMessage(query, true);
    setIsLoading(true);
    setShouldAutoScroll(true);
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    try {
      const response = await chatService.sendMessage(
        query, 
        restaurantProfile.business_unit_id, 
        chatHistory,
        abortControllerRef.current.signal
      );
      addMessage(response, false);
    } catch (error: any) {
      if (error.name === 'AbortError') {
        addMessage('Query cancelled by user.', false);
      } else {
        console.error('Error sending suggestion:', error);
        addMessage('Sorry, I encountered an error. Please try again.', false);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleTransferToSaveForm = () => {
    if (messages.length < 2) {
      toast.error('Need at least one question and answer to save analysis');
      return;
    }

    // Get the last assistant message (analysis) and the user query before it
    const lastAssistantMessage = [...messages].reverse().find(msg => !msg.isUser);
    const lastUserMessage = [...messages].reverse().find(msg => msg.isUser);

    if (!lastAssistantMessage || !lastUserMessage) {
      toast.error('Could not find a complete question-answer pair');
      return;
    }

    // Extract tags from user query (simple keyword extraction)
    const extractTags = (text: string): string[] => {
      const commonWords = ['el', 'la', 'los', 'las', 'de', 'del', 'y', 'o', 'en', 'con', 'por', 'para', 'que', 'como', 'mi', 'mis', 'un', 'una'];
      return text.toLowerCase()
        .split(/[^a-záéíóúñü]+/)
        .filter(word => word.length > 2 && !commonWords.includes(word))
        .slice(0, 5); // Limit to 5 tags
    };

    // Determine analysis type based on keywords
    const determineAnalysisType = (query: string, response: string): string => {
      const text = (query + ' ' + response).toLowerCase();
      if (text.includes('precio') || text.includes('cost') || text.includes('ahorro')) return 'price_analysis';
      if (text.includes('proveedor') || text.includes('supplier')) return 'supplier_analysis';
      if (text.includes('producto') || text.includes('ingredient')) return 'product_analysis';
      if (text.includes('tendencia') || text.includes('trend')) return 'trend_analysis';
      return 'general_analysis';
    };

    // Extract potential recommendations (lines that start with action words)
    const extractRecommendations = (text: string): string[] => {
      const lines = text.split('\n');
      const actionWords = ['considera', 'ajusta', 'busca', 'cambia', 'optimiza', 'mejora', 'reduce', 'aumenta'];
      return lines
        .filter(line => actionWords.some(word => line.toLowerCase().includes(word)))
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .slice(0, 5); // Limit to 5 recommendations
    };

    setAnalysisForm({
      originalQuery: lastUserMessage.content,
      analysisText: lastAssistantMessage.content,
      analysisType: determineAnalysisType(lastUserMessage.content, lastAssistantMessage.content),
      importance: 'medium',
      tags: extractTags(lastUserMessage.content),
      recommendations: extractRecommendations(lastAssistantMessage.content),
      dataSummary: {} // Could be enhanced to extract numerical data from response
    });

    setShowSaveForm(true);
    toast.success('Analysis transferred to save form');
  };

  const handleSaveAnalysis = async () => {
    if (!restaurantProfile) {
      toast.error('No business unit selected');
      return;
    }

    if (!analysisForm.analysisText.trim() || !analysisForm.originalQuery.trim()) {
      toast.error('Analysis text and original query are required');
      return;
    }

    setIsSaving(true);

    try {
      const insight: AnalysisInsight = {
        analysis_text: analysisForm.analysisText,
        analysis_type: analysisForm.analysisType,
        business_unit_id: restaurantProfile.business_unit_id,
        data_summary: analysisForm.dataSummary,
        importance: analysisForm.importance,
        original_query: analysisForm.originalQuery,
        recommendations: analysisForm.recommendations.filter(r => r.trim().length > 0),
        tags: analysisForm.tags.filter(t => t.trim().length > 0)
      };

      await analysisInsightsApiService.saveAnalysisInsight(insight);
      toast.success('Analysis saved successfully!');
      setShowSaveForm(false);
      
      // Reset form
      setAnalysisForm({
        originalQuery: '',
        analysisText: '',
        analysisType: 'general_analysis',
        importance: 'medium',
        tags: [],
        recommendations: [],
        dataSummary: {}
      });
    } catch (error) {
      console.error('Error saving analysis:', error);
      toast.error('Failed to save analysis. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const addTag = (tag: string) => {
    if (tag.trim() && !analysisForm.tags.includes(tag.trim())) {
      setAnalysisForm(prev => ({
        ...prev,
        tags: [...prev.tags, tag.trim()]
      }));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setAnalysisForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addRecommendation = (recommendation: string) => {
    if (recommendation.trim()) {
      setAnalysisForm(prev => ({
        ...prev,
        recommendations: [...prev.recommendations, recommendation.trim()]
      }));
    }
  };

  const removeRecommendation = (index: number) => {
    setAnalysisForm(prev => ({
      ...prev,
      recommendations: prev.recommendations.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold tracking-tight">AI Assistant</h1>
        {restaurantProfile && (
          <div className="bg-card border rounded-lg px-4 py-2">
            <div className="text-xs text-muted-foreground">Business Unit</div>
            <div className="font-semibold text-primary">ID: {restaurantProfile.business_unit_id}</div>
            <div className="text-sm">{restaurantProfile.business_unit_name || 'Default Unit'}</div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Card className="h-[calc(100vh-240px)] flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recipe Cost AI</CardTitle>
                  <CardDescription>
                    Ask questions about recipe costs, ingredient pricing, and optimization
                  </CardDescription>
                </div>
                <Button
                  onClick={handleTransferToSaveForm}
                  variant="outline"
                  size="sm"
                  disabled={messages.length < 2}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Save Analysis
                </Button>
              </div>
            </CardHeader>
            <CardContent ref={chatContainerRef} className="flex-grow overflow-y-auto p-4 bg-gradient-to-b from-background to-muted/20">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="p-6 rounded-full bg-primary/10 mb-6">
                    <Bot className="h-12 w-12 text-primary" />
                  </div>
                  <h3 className="text-xl font-medium mb-2">How can I help you today?</h3>
                  <p className="text-muted-foreground max-w-md leading-relaxed">
                    Ask me about recipe costs, ingredient pricing, menu optimization, or any other culinary insights you need.
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {messages.map((msg) => (
                    <MessageBubble key={msg.id} message={msg} />
                  ))}
                </div>
              )}
              {isLoading && (
                <div className="flex items-start gap-3 max-w-[85%] mb-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <div className="flex space-x-2 items-center">
                      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "0ms" }}></div>
                      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "150ms" }}></div>
                      <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: "300ms" }}></div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="border-t p-4">
              <form 
                className="flex w-full gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isLoading) {
                    handleSend();
                  }
                }}
              >
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about recipe costs..."
                  className="flex-grow"
                  disabled={isLoading}
                />
                {isLoading ? (
                  <Button 
                    type="button" 
                    size="icon"
                    variant="destructive"
                    onClick={handleStop}
                    title="Stop query"
                  >
                    <StopCircle className="h-5 w-5" />
                  </Button>
                ) : (
                  <Button type="submit" size="icon">
                    <Send className="h-5 w-5" />
                  </Button>
                )}
              </form>
            </CardFooter>
          </Card>
        </div>

        <div className="space-y-4">
          <h2 className="text-lg font-medium">Sugerencias de Consulta</h2>
          
          {/* Price Analysis */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">📊 Análisis de Precios</h3>
            <Suggestion
              title="Historial de Precios"
              description="Seguimiento histórico y variaciones estacionales"
              icon={TrendingUp}
              queryTemplate="Muéstrame el historial de precios y variaciones estacionales para {input}"
              hasInput={true}
              inputPlaceholder="ej: tomate, aceite de oliva"
              onSend={handleSuggestionSend}
            />
            <Suggestion
              title="Top 5 Productos de Mayor Impacto"
              description="Identifica los productos más caros"
              icon={BarChart3}
              queryTemplate="¿Cuáles son mis top 5 productos que más impactan en mi coste global?"
              onSend={handleSuggestionSend}
            />
          </div>

          {/* Savings Analysis */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">💰 Análisis de Ahorro</h3>
            <Suggestion
              title="Oportunidades de Ahorro"
              description="Productos donde pagas más que el mercado"
              icon={DollarSign}
              queryTemplate="Muéstrame oportunidades de ahorro donde mi coste está por encima del promedio del mercado"
              onSend={handleSuggestionSend}
            />
            <Suggestion
              title="Sustitución de Productos"
              description="Sugiere alternativas más económicas"
              icon={Package}
              queryTemplate="Sugiere sustituciones más baratas para {input}"
              hasInput={true}
              inputPlaceholder="ej: res, salmón"
              onSend={handleSuggestionSend}
            />
          </div>

          {/* Supplier Comparison */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">🤝 Comparación de Proveedores</h3>
            <Suggestion
              title="Mejor Proveedor por Producto"
              description="Encuentra el mejor proveedor para un producto"
              icon={Users}
              queryTemplate="¿Quién es el mejor proveedor para {input}? Muestra comparación de precios"
              hasInput={true}
              inputPlaceholder="ej: aceite de oliva, queso"
              onSend={handleSuggestionSend}
            />
            <Suggestion
              title="Proveedor Más Competitivo"
              description="Identifica el proveedor más competitivo en general"
              icon={Sparkles}
              queryTemplate="¿Cuál proveedor es más competitivo en promedio para todos los productos?"
              onSend={handleSuggestionSend}
            />
          </div>

          {/* Combined Queries */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">🔍 Análisis Combinado</h3>
            <Suggestion
              title="Optimizar Costos por Categoría"
              description="Encuentra ahorros en una categoría específica"
              icon={Package}
              queryTemplate="¿Cómo puedo optimizar costos en {input}? Muestra sustitutos y mejores proveedores"
              hasInput={true}
              inputPlaceholder="ej: verduras, lácteos"
              onSend={handleSuggestionSend}
            />
            <Suggestion
              title="Oportunidades de Negociación"
              description="Productos con margen de negociación"
              icon={TrendingUp}
              queryTemplate="¿Qué productos tienen mayor margen de negociación basado en benchmarks del mercado?"
              onSend={handleSuggestionSend}
            />
          </div>
        </div>
      </div>

      {/* Save Analysis Form Modal */}
      {showSaveForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Save className="h-5 w-5" />
                    Save Analysis Insights
                  </CardTitle>
                  <CardDescription>
                    Review and save the analysis for future reference
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setShowSaveForm(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Original Query */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Original Query</label>
                  <textarea
                    value={analysisForm.originalQuery}
                    onChange={(e) => setAnalysisForm(prev => ({ ...prev, originalQuery: e.target.value }))}
                    className="w-full p-3 border border-border rounded-md text-sm min-h-[80px] bg-background text-foreground resize-none"
                    placeholder="Enter the original query..."
                  />
                </div>

                {/* Analysis Type and Importance */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Analysis Type</label>
                    <select
                      value={analysisForm.analysisType}
                      onChange={(e) => setAnalysisForm(prev => ({ ...prev, analysisType: e.target.value }))}
                      className="w-full p-2 border border-border rounded-md text-sm bg-background text-foreground"
                    >
                      <option value="general_analysis">General Analysis</option>
                      <option value="price_analysis">Price Analysis</option>
                      <option value="supplier_analysis">Supplier Analysis</option>
                      <option value="product_analysis">Product Analysis</option>
                      <option value="trend_analysis">Trend Analysis</option>
                      <option value="cost_optimization">Cost Optimization</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Importance</label>
                    <select
                      value={analysisForm.importance}
                      onChange={(e) => setAnalysisForm(prev => ({ ...prev, importance: e.target.value as 'low' | 'medium' | 'high' }))}
                      className="w-full p-2 border border-border rounded-md text-sm bg-background text-foreground"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Tags</label>
                  <div className="flex flex-wrap gap-2 mb-2 min-h-[32px] p-2 border border-border rounded-md bg-muted/30">
                    {analysisForm.tags.length > 0 ? (
                      analysisForm.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
                        >
                          {tag}
                          <button
                            onClick={() => removeTag(tag)}
                            className="hover:text-primary/70"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">No tags added yet</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a tag..."
                      className="text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addTag((e.target as HTMLInputElement).value);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                    />
                    <Button
                      onClick={() => {
                        const input = document.querySelector('input[placeholder="Add a tag..."]') as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addTag(input.value);
                          input.value = '';
                        }
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Analysis Text */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Analysis Response</label>
                  <textarea
                    value={analysisForm.analysisText}
                    onChange={(e) => setAnalysisForm(prev => ({ ...prev, analysisText: e.target.value }))}
                    className="w-full p-3 border border-border rounded-md text-sm min-h-[200px] bg-background text-foreground resize-none"
                    placeholder="Enter the analysis response..."
                  />
                </div>

                {/* Recommendations */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground">Recommendations</label>
                  <div className="max-h-[120px] overflow-y-auto space-y-2 p-2 border border-border rounded-md bg-muted/30">
                    {analysisForm.recommendations.length > 0 ? (
                      analysisForm.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-start gap-2 p-2 bg-background rounded-md">
                          <span className="flex-1 text-sm text-foreground">{rec}</span>
                          <button
                            onClick={() => removeRecommendation(index)}
                            className="text-muted-foreground hover:text-destructive mt-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground p-2">No recommendations added yet</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add a recommendation..."
                      className="text-sm"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addRecommendation((e.target as HTMLInputElement).value);
                          (e.target as HTMLInputElement).value = '';
                        }
                      }}
                    />
                    <Button
                      onClick={() => {
                        const input = document.querySelector('input[placeholder="Add a recommendation..."]') as HTMLInputElement;
                        if (input && input.value.trim()) {
                          addRecommendation(input.value);
                          input.value = '';
                        }
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>

          </CardContent>
          <CardFooter className="border-t flex justify-end gap-3 p-6">
            <Button
              onClick={() => setShowSaveForm(false)}
              variant="outline"
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAnalysis}
              disabled={isSaving}
              className="flex items-center gap-2 min-w-[140px]"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save Analysis
                </>
              )}
            </Button>
          </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};

// Import the TrendingUp icon used in the Suggestion component but not at the top imports

export default AIAssistant;
